import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { mainSiteUrl } from '@/mocks/homeData';

export default function BasetaoShippingCalculator() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main className="pt-20">
        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="text-center mb-10">
            <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-4">Calculator</span>
            <h1 className="text-2xl md:text-4xl font-heading text-foreground-950 mb-4">BaseTao Shipping Calculator: Estimate Your Delivery Costs</h1>
            <p className="text-foreground-500 text-sm md:text-base max-w-xl mx-auto">A comprehensive guide to calculating and comparing BaseTao shipping costs across different methods and destinations.</p>
          </div>

          <div className="prose-custom space-y-8">
            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">How BaseTao Shipping Costs Are Calculated</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">The <strong className="text-foreground-900">BaseTao Shipping Calculator</strong> approach helps you estimate delivery costs before committing to a purchase. Shipping costs are determined by several key factors: package weight, package dimensions (volumetric weight), destination country, shipping method, and any additional services like insurance or express handling.</p>
              <p className="text-foreground-600 leading-relaxed">Understanding these factors upfront allows you to budget accurately and avoid surprises when your final shipping invoice arrives.</p>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Key Factors That Affect Shipping Cost</h2>
              <div className="overflow-hidden rounded-lg border border-background-200/70">
                <table className="w-full text-sm">
                  <thead><tr className="bg-background-100"><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Factor</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">Impact</th><th className="text-left px-4 py-2.5 font-heading text-foreground-900 text-xs">How to Optimize</th></tr></thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[{f:'Weight',i:'High',o:'Consolidate multiple items into one shipment'},{f:'Volume',i:'High',o:'Request compact packaging to reduce box size'},{f:'Destination',i:'High',o:'Compare rates for your specific country'},{f:'Shipping Method',i:'High',o:'Balance speed vs cost — slower is usually cheaper'},{f:'Insurance',i:'Medium',o:'Evaluate item value before adding coverage'},{f:'Peak Season',i:'Medium',o:'Avoid major holiday shipping rushes when possible'}].map((r,i)=>(<tr key={i}><td className="px-4 py-2.5 text-foreground-700 font-medium text-xs">{r.f}</td><td className="px-4 py-2.5"><span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${r.i==='High'?'bg-accent-100 text-accent-700':'bg-secondary-100 text-secondary-700'}`}>{r.i}</span></td><td className="px-4 py-2.5 text-foreground-500 text-xs">{r.o}</td></tr>))}
                  </tbody>
                </table>
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading text-foreground-900 mb-4">Using the Spreadsheet to Calculate Shipping</h2>
              <p className="text-foreground-600 leading-relaxed mb-4">Your BaseTao Spreadsheet can double as a shipping calculator. Add columns for estimated weight, shipping method, and estimated cost. As you add products, use a formula column to calculate a rough total based on an average cost-per-kg for your destination.</p>
              <p className="text-foreground-600 leading-relaxed">For country-specific guidance, see our <Link to="/basetao-shipping-usa/" className="text-primary-600 hover:text-primary-700 font-medium">USA shipping guide</Link> and our <Link to="/basetao-shipping-cost/" className="text-primary-600 hover:text-primary-700 font-medium">general shipping cost resource</Link>.</p>
            </section>

            <div className="bg-background-100 border border-background-200/70 rounded-xl p-6 mt-10">
              <h3 className="font-heading text-lg text-foreground-900 mb-3">Estimate Your Shipping</h3>
              <p className="text-foreground-600 text-sm mb-4">Start building your shipment and estimate costs with your BaseTao Spreadsheet.</p>
              <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-accent-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap">Visit BaseTao Spreadsheet <div className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line text-sm"></i></div></a>
            </div>

            <div className="text-sm text-foreground-400 mt-8 pt-6 border-t border-background-200/60 flex flex-wrap gap-4">
              <Link to="/" className="hover:text-primary-600 transition-colors">&larr; Back to Home</Link>
              <Link to="/basetao-shipping-cost/" className="hover:text-primary-600 transition-colors">Shipping Cost Guide</Link>
              <Link to="/basetao-cost-tracker/" className="hover:text-primary-600 transition-colors">Cost Tracker</Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Shipping Calculator: Estimate Your Delivery Costs',
            description: 'A comprehensive guide to calculating and comparing BaseTao shipping costs across different methods and destinations.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-shipping-calculator/',
            },
          }),
        }}
      />
    </div>
  );
}